import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { BrandSocialLink, BrandSocialLinkSettings, SocialLinkTypes } from "@app/models";
import { BrandSocialLinkService } from "@app/services";
import { Unsubscriber } from "@app/shared/components";
import { Patterns } from "@app/shared/validators";
import { startWith } from "rxjs/operators";

@Component({
  selector: "app-social-links-form-control",
  templateUrl: "./social-links-form-control.component.html",
  styleUrls: ["./social-links-form-control.component.scss"]
})
export class SocialLinksFormControlComponent extends Unsubscriber implements OnInit {
  @Input() isRuleBtn = false;
  @Input() isFull = false;
  @Input() isListOnlyRead = false;
  @Input() brandId: number;
  @Input() isEditable = false;

  @Input() set links(value: BrandSocialLink[]) {
    if (Array.isArray(value) && !this._links) {
      this._links = value;
      this.patchSocialLinksForm(this._links);

      if (this.isEditable) {
        this.subscribeChanges();
      }

      this.brandSocialLinkService.onUpdateLinks(this._links);
      this.cdr.detectChanges();
    }
  }

  @Output() linksChanged = new EventEmitter<BrandSocialLink[]>();
  @Output() linksValid = new EventEmitter<boolean>();

  readonly validators: Array<ValidatorFn> = [Validators.pattern(Patterns.url), Validators.maxLength(2000)];
  readonly defaultLinks: BrandSocialLinkSettings[] = [
    {
      linkType: SocialLinkTypes.Facebook,
      icon: "mdi mdi-facebook",
      color: "primary",
      defaultPlaceholder: "https://www.facebook.com/",
      isShowLink: true,
      iconSvg: "assets/images/social-icons/facebook.svg"
    },
    {
      linkType: SocialLinkTypes.Instagram,
      icon: "mdi mdi-instagram",
      color: "pink",
      defaultPlaceholder: "https://www.instagram.com/",
      isShowLink: true,
      iconSvg: "assets/images/social-icons/instagram.svg"
    },
    {
      linkType: SocialLinkTypes.LinkedIn,
      icon: "mdi mdi-linkedin",
      color: "primary",
      defaultPlaceholder: "https://www.linkedin.com/",
      isShowLink: true,
      iconSvg: "assets/images/social-icons/linkedin.svg"
    },
    {
      linkType: SocialLinkTypes.Google,
      icon: "mdi mdi-google",
      color: "danger",
      defaultPlaceholder: "https://www.google.com/",
      isShowLink: false,
      iconSvg: "assets/images/social-icons/google.svg"
    },
    {
      linkType: SocialLinkTypes.Snapchat,
      icon: "mdi mdi-snapchat",
      color: "warning",
      defaultPlaceholder: "https://www.snapchat.com/",
      isShowLink: false,
      iconSvg: "assets/images/social-icons/snapchat.svg"
    },
    {
      linkType: SocialLinkTypes.Twitter,
      icon: "mdi mdi-twitter",
      color: "info",
      defaultPlaceholder: "https://twitter.com/",
      isShowLink: false,
      iconSvg: "assets/images/social-icons/twitter.svg"
    },
    {
      linkType: SocialLinkTypes.Youtube,
      icon: "mdi mdi-youtube",
      color: "dark-danger",
      defaultPlaceholder: "https://www.youtube.com/",
      isShowLink: false,
      iconSvg: "assets/images/social-icons/youtube.svg"
    },
    {
      linkType: SocialLinkTypes.TikTok,
      icon: "bx bxl-tiktok-old",
      color: "dark",
      defaultPlaceholder: "https://tiktok.com/",
      isShowLink: false,
      iconSvg: "assets/images/social-icons/tiktok.svg"
    }
  ];

  ruleBtn = true;
  titleRuleBtn = "Show more";
  form: FormGroup = this.initForm();

  private _links: BrandSocialLink[] = null;

  constructor(private brandSocialLinkService: BrandSocialLinkService, private cdr: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    const links = this.defaultLinks.map((l) => {
      return {
        id: l.id,
        linkType: l.linkType,
        linkUrl: ""
      } as BrandSocialLink;
    });
    this.brandSocialLinkService.onUpdateLinks(links);
  }

  getLinkUrl(type: SocialLinkTypes): string {
    return this._links?.find((l) => l.linkType === type)?.linkUrl;
  }

  showMoreLink(valueBtn: boolean): void {
    this.ruleBtn = !valueBtn;

    if (!this.ruleBtn) {
      this.titleRuleBtn = "Show less";
      this.defaultLinks.forEach((e) => {
        if (!e.isShowLink) {
          e.isShowLink = true;
        }
      });
    } else {
      this.titleRuleBtn = "Show more";
      let i = 0;
      this.defaultLinks.forEach((e) => {
        if (i < 3) {
          e.isShowLink = true;
          i = i + 1;
        } else {
          e.isShowLink = false;
        }
      });
    }
  }

  private subscribeChanges(): void {
    this.sub = this.form.valueChanges.pipe(startWith(this.form.getRawValue())).subscribe(() => {
      const links: BrandSocialLink[] = this.generateLinks();

      if (this.form.valid) {
        this.brandSocialLinkService.onUpdateLinks(links);
        this.linksChanged.emit(links);
      }
      this.linksValid.emit(this.form.valid);
      this.brandSocialLinkService.onUpdateLinksValidation(this.form.valid);
    });
  }

  private generateLinks(): BrandSocialLink[] {
    const links: BrandSocialLink[] = [];
    Object.keys(this.form.controls).forEach((key) => {
      const id = this.defaultLinks?.filter((l) => l.linkType === +key)[0].id;
      const link = {
        linkUrl: this.form.controls[key].value,
        linkType: +key,
        id: id
      } as BrandSocialLink;
      links.push(link);
    });
    return links;
  }

  private initForm(): FormGroup {
    const form = {};
    this.defaultLinks.forEach((links) => {
      form[links.linkType] = new FormControl("", this.validators);
    });
    return new FormGroup(form);
  }

  private patchSocialLinksForm(links: BrandSocialLink[]) {
    const formValues = [];
    this.defaultLinks.forEach((link) => {
      link.id = links.find((l) => l.linkType === link.linkType)?.id;
      const formLink = links.find((l) => l.linkType === link.linkType)?.linkUrl;
      formValues.push({ [link.linkType]: formLink ? formLink : "" });
    });
    const patchObject = Object.assign({}, ...formValues.map((fl) => fl));
    this.form.patchValue(patchObject, { emitEvent: false });
  }
}
